import React, { useState, useEffect } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { 
  Bell, 
  BellRing,
  Clock,
  UserCircle,
  Droplets,
  Flame,
  Loader2,
  Save,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const NotificationPreferences = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    meal_reminder_hours: 8,
    coach_ai_reminder_days: 3,
    quiet_hours_start: 21,
    quiet_hours_end: 8,
    hydration_reminder: true,
    streak_warning: true,
    max_daily_notifications: 3
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/notifications/settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(
        language === 'fr' 
          ? 'Préférences sauvegardées!' 
          : 'Preferences saved!'
      );
    } catch (error) {
      toast.error(
        language === 'fr'
          ? 'Erreur lors de la sauvegarde'
          : 'Error saving preferences'
      );
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="bg-card/50 border-border" data-testid="notification-preferences">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <BellRing className="w-5 h-5 text-primary" />
          {language === 'fr' ? 'Préférences de notifications' : 'Notification Preferences'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Meal Reminder Hours */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            <Label className="text-sm font-medium">
              {language === 'fr' 
                ? 'Rappel repas après (heures sans repas)' 
                : 'Meal reminder after (hours without meal)'}
            </Label>
          </div>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.meal_reminder_hours]}
              onValueChange={(value) => updateSetting('meal_reminder_hours', value[0])}
              min={4}
              max={12}
              step={1}
              className="flex-1"
              data-testid="meal-reminder-slider"
            />
            <span className="text-sm font-mono bg-secondary px-2 py-1 rounded min-w-[3rem] text-center">
              {settings.meal_reminder_hours}h
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {language === 'fr'
              ? `Tu recevras un rappel si tu n'as pas logué de repas depuis ${settings.meal_reminder_hours} heures.`
              : `You'll receive a reminder if you haven't logged a meal in ${settings.meal_reminder_hours} hours.`}
          </p>
        </div>

        {/* Coach Yuri Reminder Days */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-blue-500" />
            <Label className="text-sm font-medium">
              {language === 'fr' 
                ? 'Rappel YUMIX après (jours)' 
                : 'YUMIX reminder after (days)'}
            </Label>
          </div>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.coach_ai_reminder_days]}
              onValueChange={(value) => updateSetting('coach_ai_reminder_days', value[0])}
              min={1}
              max={7}
              step={1}
              className="flex-1"
              data-testid="coach-ai-reminder-slider"
            />
            <span className="text-sm font-mono bg-secondary px-2 py-1 rounded min-w-[3rem] text-center">
              {settings.coach_ai_reminder_days}j
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {language === 'fr'
              ? `Tu recevras une invitation à discuter avec YUMIX après ${settings.coach_ai_reminder_days} jours d'inactivité.`
              : `You'll receive an invite to chat with YUMIX after ${settings.coach_ai_reminder_days} days of inactivity.`}
          </p>
        </div>

        {/* Quiet Hours */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-purple-500" />
            <Label className="text-sm font-medium">
              {language === 'fr' ? 'Plage horaire active' : 'Active hours'}
            </Label>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {language === 'fr' ? 'Notifications de' : 'Notifications from'}
            </span>
            <span className="font-mono bg-secondary px-2 py-1 rounded">
              {settings.quiet_hours_end}:00
            </span>
            <span className="text-muted-foreground">
              {language === 'fr' ? 'à' : 'to'}
            </span>
            <span className="font-mono bg-secondary px-2 py-1 rounded">
              {settings.quiet_hours_start}:00
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {language === 'fr'
              ? 'Aucune notification ne sera envoyée en dehors de ces heures.'
              : 'No notifications will be sent outside these hours.'}
          </p>
        </div>

        {/* Toggle Options */}
        <div className="space-y-4 pt-2">
          {/* Hydration Reminder */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Droplets className="w-5 h-5 text-cyan-500" />
              <div>
                <p className="text-sm font-medium">
                  {language === 'fr' ? 'Rappels hydratation' : 'Hydration reminders'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Rappels pour boire de l\'eau' 
                    : 'Reminders to drink water'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.hydration_reminder}
              onCheckedChange={(checked) => updateSetting('hydration_reminder', checked)}
              data-testid="hydration-reminder-toggle"
            />
          </div>

          {/* Streak Warning */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Flame className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm font-medium">
                  {language === 'fr' ? 'Alerte série en danger' : 'Streak at risk alert'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Notification si ta série risque d\'être brisée' 
                    : 'Notification if your streak is at risk'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.streak_warning}
              onCheckedChange={(checked) => updateSetting('streak_warning', checked)}
              data-testid="streak-warning-toggle"
            />
          </div>
        </div>

        {/* Max Daily */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2">
            <BellRing className="w-4 h-4 text-yellow-500" />
            <Label className="text-sm font-medium">
              {language === 'fr' 
                ? 'Maximum de notifications par jour' 
                : 'Maximum notifications per day'}
            </Label>
          </div>
          <div className="flex items-center gap-4">
            <Slider
              value={[settings.max_daily_notifications]}
              onValueChange={(value) => updateSetting('max_daily_notifications', value[0])}
              min={1}
              max={5}
              step={1}
              className="flex-1"
              data-testid="max-daily-slider"
            />
            <span className="text-sm font-mono bg-secondary px-2 py-1 rounded min-w-[3rem] text-center">
              {settings.max_daily_notifications}
            </span>
          </div>
        </div>

        {/* Save Button */}
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="save-notification-settings"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : saved ? (
            <Check className="w-4 h-4 mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saved 
            ? (language === 'fr' ? 'Sauvegardé!' : 'Saved!') 
            : (language === 'fr' ? 'Sauvegarder' : 'Save')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;
